import React, { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createApiClient } from '@center-technologies/shared-api-clients';
import {
  WaitlistStatusPageOrganism,
  type WaitlistStatusResult,
} from 'design-system/my-lib';

interface WaitlistStatusApiResponse {
  status: 'pending' | 'approved' | 'rejected';
}

interface ApiError {
  response?: { status?: number };
}

const isNotFoundError = (err: unknown): boolean => (err as ApiError)?.response?.status === 404;

/**
 * Public, unauthenticated route — no Descope session exists here, so the ApiClients
 * context (which gates every client behind `authOverride.isAuthenticated`) never
 * becomes ready. `GET /auth/waitlist/status/:email` is itself unprotected
 * (`unprotectedStartsWith: ['/auth/waitlist']`), so this builds its own client with
 * no token provider rather than blocking on a session that will never exist.
 */
const WaitlistStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') ?? undefined;

  const organizationClient = useMemo(
    () =>
      createApiClient(process.env.REACT_APP_ORGANIZATION_ENDPOINT || 'http://localhost:3003', {
        tokenProvider: () => Promise.resolve(undefined),
      }),
    [],
  );

  const checkStatus = useCallback(
    async (email: string): Promise<WaitlistStatusResult> => {
      try {
        const response = await organizationClient.get<WaitlistStatusApiResponse>(
          `/auth/waitlist/status/${encodeURIComponent(email)}`,
        );
        return { status: response.status };
      } catch (err) {
        if (isNotFoundError(err)) {
          return { status: 'not_found' };
        }
        throw err;
      }
    },
    [organizationClient],
  );

  const handleNavigateToSignIn = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return (
    <WaitlistStatusPageOrganism
      initialEmail={initialEmail}
      checkStatus={checkStatus}
      onNavigateToSignIn={handleNavigateToSignIn}
    />
  );
};

export default WaitlistStatusPage;
